import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, BookOpen, RotateCw } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";
import CareNewsCard from "../components/shared/CareNewsCard";
import ArticleSearchBar from "../components/shared/ArticleSearchBar";
import TrafficLight from "../components/shared/TrafficLight";
import SEOHead from "../components/shared/SEOHead";
import { api } from "../lib/api";
import { NEWS_CATEGORIES as CATEGORIES } from "../lib/articleCategories";
import { articleMatchesSearch } from "../lib/articleSearch";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function CareNews() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [nonce, setNonce] = useState(0);
  const searchActive = search.trim().length > 0;
  const searchResults = searchActive
    ? items.filter((i) => articleMatchesSearch(i, search, ["title", "summary", "category"]))
    : [];
  const clearSearchAndFilters = () => {
    setSearch("");
    setCat("All");
  };

  const fetchItems = useCallback((signal) => {
    setStatus("loading");
    const params = cat === "All" ? {} : { category: cat };
    return api
      .get("/api/news", { params, signal })
      .then((r) => {
        setItems(r.data.items || []);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setStatus("error");
      });
  }, [cat]);

  // Load on mount + every category change. AbortController prevents races
  // when the user clicks categories quickly or navigates away.
  useEffect(() => {
    const ctrl = new AbortController();
    fetchItems(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchItems, nonce]);

  // If the browser restores this page from bfcache (back/forward navigation)
  // we re-fetch so the grid always reflects the latest server state instead
  // of any stale or empty in-memory list.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setNonce((n) => n + 1);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div data-testid="page-care-news">
      <SEOHead
        title="Care News & Policy Watch — UK care industry updates"
        description="Plain-English policy, funding, regulatory and employment-law updates affecting independent carers and the families who hire them. Calm, factual, no scaremongering."
        path="/care-news"
      />

      <PageHero
        eyebrow="Care News & Policy Watch"
        title="Care Industry News & Updates"
        description="Plain-English policy, funding and regulatory updates that affect independent carers and the families who hire them. Calm, factual, and written for the people doing the work."
      />

      {/* INTRO CONTENT — preserved verbatim as supplied */}
      <section className="section pt-12 md:pt-16">
        <div className="container-jcc max-w-3xl space-y-6 text-ink-muted leading-relaxed" data-testid="care-news-intro">
          <p>
            The care industry is constantly changing — but most updates are written in legal language, buried in reports, or shared without context.
          </p>
          <p>
            Care News &amp; Policy Watch exists to translate what's happening into clear, practical information for self-employed carers and families using private care.
          </p>
          <p>
            We monitor legislation, guidance, funding changes, and industry movements so you don't have to.
          </p>

          <div>
            <h2 className="font-display text-2xl font-semibold text-ink mt-10 mb-3">What we cover</h2>
            <ul className="space-y-2 list-disc list-inside marker:text-teal">
              <li>UK care legislation and policy changes</li>
              <li>Guidance affecting self-employed and live-in carers</li>
              <li>Council and Direct Payments developments</li>
              <li>Workforce and compliance issues</li>
              <li>Industry trends shaping the future of independent care</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl font-semibold text-ink mt-10 mb-3">How we explain it</h2>
            <p>Every update is written in:</p>
            <ul className="space-y-2 list-disc list-inside marker:text-teal mt-2">
              <li>Plain English</li>
              <li>Clear, calm language</li>
              <li>Practical terms carers can understand</li>
            </ul>
            <p className="mt-3">No scaremongering. No jargon. No politics.</p>
          </div>

          <div>
            <h2 className="font-display text-2xl font-semibold text-ink mt-10 mb-3">What you'll see in every update</h2>
            <ul className="space-y-2 list-disc list-inside marker:text-teal">
              <li>What's changed</li>
              <li>Who it affects</li>
              <li>What it means in real life</li>
              <li>Whether you need to take action</li>
              <li>Which JCC tools help protect you</li>
            </ul>
          </div>

          {/* Traffic light legend integrated naturally into the intro */}
          <div className="mt-10 rounded-2xl border border-slate-200 bg-surface p-6" data-testid="traffic-light-legend">
            <h3 className="font-display text-lg font-semibold text-ink mb-3">The JCC Traffic Light system</h3>
            <p className="text-sm">Every Care News update is tagged with a quick visual indicator so you can see at a glance how relevant it is to your situation:</p>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <div className="flex items-start gap-3">
                <TrafficLight level="green" size="md" showLabel={false} />
                <div className="text-sm">
                  <div className="font-semibold text-ink">Awareness</div>
                  <div className="text-ink-muted">Low concern — worth knowing about.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <TrafficLight level="amber" size="md" showLabel={false} />
                <div className="text-sm">
                  <div className="font-semibold text-ink">Watch closely</div>
                  <div className="text-ink-muted">Changes developing — keep an eye out.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <TrafficLight level="red" size="md" showLabel={false} />
                <div className="text-sm">
                  <div className="font-semibold text-ink">High impact</div>
                  <div className="text-ink-muted">Urgent or major change — action may be needed.</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl font-semibold text-ink mt-10 mb-3">Our approach</h2>
            <p>JCC supports fair, compliant, independent care that protects both carers and families.</p>
            <p className="mt-3">We believe carers deserve:</p>
            <ul className="space-y-2 list-disc list-inside marker:text-teal mt-2">
              <li>clarity instead of confusion</li>
              <li>guidance instead of fear</li>
              <li>support instead of silence</li>
            </ul>
            <p className="mt-3">This page is part of that commitment.</p>
            <p className="mt-3 italic text-ink-soft">Updated regularly. Curated carefully. Always written with carers in mind.</p>
          </div>
        </div>
      </section>

      {/* ARTICLE INDEX — mirrors WIM Blog layout exactly */}
      <section className="section pt-0">
        <div className="container-jcc">
          <ArticleSearchBar value={search} onChange={setSearch} testId="care-news-search" />
          <div className="flex flex-wrap gap-2 mb-10" data-testid="care-news-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`news-cat-${c.toLowerCase().replace(/[ &]+/g, "-")}`}
                className={`pill px-3 py-1.5 ${
                  cat === c ? "bg-ink text-white" : "bg-slate-100 text-ink-muted hover:bg-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {status === "loading" && items.length === 0 && (
            <div className="py-20 text-center text-ink-muted" data-testid="care-news-loading">
              Loading updates…
            </div>
          )}

          {status === "error" && (
            <div
              className="card-base p-8 text-center max-w-xl mx-auto"
              data-testid="care-news-error"
            >
              <h3 className="font-display text-xl font-semibold mb-2">We couldn't load updates</h3>
              <p className="text-ink-muted text-sm mb-5">
                Something went wrong loading Care News. Please try again — your connection may have dropped.
              </p>
              <button
                type="button"
                onClick={() => setNonce((n) => n + 1)}
                className="btn-teal inline-flex"
                data-testid="care-news-retry"
              >
                <RotateCw className="w-4 h-4" /> Try again
              </button>
            </div>
          )}

          {status === "ok" && items.length === 0 && (
            <div className="card-base p-8 text-center max-w-xl mx-auto" data-testid="care-news-empty">
              <h3 className="font-display text-xl font-semibold mb-2">Nothing to report just yet</h3>
              <p className="text-ink-muted text-sm">
                No Care News updates in this category right now. New plain-English policy
                briefings are added regularly — check back soon or browse{" "}
                <button
                  type="button"
                  onClick={() => setCat("All")}
                  className="text-teal hover:underline font-medium"
                >
                  all updates
                </button>
                .
              </p>
            </div>
          )}

          {status === "ok" && items.length > 0 && searchActive && searchResults.length === 0 && (
            <div className="card-base p-8 text-center max-w-xl mx-auto" data-testid="care-news-search-empty">
              <h3 className="font-display text-xl font-semibold mb-2">No articles found</h3>
              <p className="text-ink-muted text-sm mb-5">
                No updates match "{search}" in this category.
              </p>
              <button
                type="button"
                onClick={clearSearchAndFilters}
                className="btn-outline inline-flex"
                data-testid="care-news-clear-search"
              >
                Clear search and filters
              </button>
            </div>
          )}

          {status === "ok" && items.length > 0 && searchActive && searchResults.length > 0 && (
            <>
              <p className="text-sm text-ink-soft mb-4" data-testid="care-news-search-results-count">
                {searchResults.length} article{searchResults.length === 1 ? "" : "s"} found
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="care-news-search-results">
                {searchResults.map((item) => (
                  <CareNewsCard key={item.slug} item={item} />
                ))}
              </div>
            </>
          )}

          {status === "ok" && items.length > 0 && !searchActive && (
            <>
              {featured && (
                <Link to={`/care-news/${featured.slug}`} className="block mb-12 group" data-testid="care-news-featured">
                  <div className="grid lg:grid-cols-5 gap-8 items-center card-base p-8">
                    {featured.featured_image && (
                      <div className="lg:col-span-2 h-48 sm:h-56 lg:h-64 rounded-xl overflow-hidden">
                        <img src={featured.featured_image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className={featured.featured_image ? "lg:col-span-3" : "lg:col-span-5"}>
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="pill bg-teal-50 text-teal">{featured.category}</span>
                        {featured.published_at && (
                          <span className="text-xs text-ink-soft flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {formatDate(featured.published_at)}
                          </span>
                        )}
                        {featured.traffic_light && (
                          <TrafficLight level={featured.traffic_light} />
                        )}
                      </div>
                      <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight group-hover:text-teal transition-colors">
                        {featured.title}
                      </h2>
                      <p className="mt-3 text-ink-muted">{featured.summary}</p>
                      {featured.source && (
                        <p className="mt-3 text-xs text-ink-soft">Source: {featured.source}</p>
                      )}
                    </div>
                  </div>
                </Link>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((item) => (
                  <CareNewsCard key={item.slug} item={item} />
                ))}
              </div>
            </>
          )}

          {/* Cross-link to WIM Blog (kept) */}
          <div
            className="mt-16 rounded-3xl border border-slate-200 bg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-testid="care-news-to-wim"
          >
            <div className="max-w-2xl">
              <div className="overline text-teal mb-2">Looking for guidance instead?</div>
              <h3 className="font-display text-2xl font-semibold leading-snug">
                The WIM Blog has evergreen articles for carers and families.
              </h3>
              <p className="mt-2 text-ink-muted text-sm">
                Practical explainers, support resources and "Why It Matters" pieces — written to help, not to chase a news cycle.
              </p>
            </div>
            <Link to="/wim-blog" className="btn-outline shrink-0">
              <BookOpen className="w-4 h-4" /> Open the WIM Blog
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
